/**
 * Badge - editor component.
 *
 * @package BrandyBlocks
 */

import { __ } from '@wordpress/i18n';
import { useState } from '@wordpress/element';
import { useBlockProps, RichText, InspectorControls } from '@wordpress/block-editor';
import { Button, RangeControl, SelectControl } from '@wordpress/components';

import PanelBody from '../../components/PanelBody';
import IconsModal from '../../components/IconsModal';
import { getIconClasses } from '../../utils/helpers';
import { ICON_POSITIONS, badgeStyle, iconStyle } from './badge-parts';

/**
 * A badge: a short label, an optional glyph beside it, and nothing else.
 *
 * The label is RichText rather than an inner block because a badge holds one
 * run of text and no more — an inner block would hand the author a paragraph
 * they could turn into anything, and a wrapper the pill has to size around.
 *
 * There is no icon colour control on purpose. The glyph is an icon font, so it
 * is painted by `color` like the text beside it; a second colour would let the
 * two drift apart with nothing gained.
 *
 * @param {Object}   props               Block props.
 * @param {Object}   props.attributes    Block attributes.
 * @param {Function} props.setAttributes Function to update attributes.
 * @return {JSX.Element} Editor component.
 */
export default function Edit( { attributes, setAttributes } ) {
	const { text, iconName, iconType, iconPosition, iconSize, style } = attributes;

	const [ isIconsModalOpen, setIsIconsModalOpen ] = useState( false );

	const blockProps = useBlockProps( { style: badgeStyle( style ) } );

	const iconClasses = getIconClasses( iconName, iconType );

	const icon = iconClasses ? (
		<i className={ iconClasses } style={ iconStyle( iconSize ) } aria-hidden="true" />
	) : null;

	return (
		<>
			<InspectorControls>
				<PanelBody title={ __( 'Icon', 'brandy-blocks' ) } initialOpen>
					<div style={ { display: 'flex', alignItems: 'center', gap: '12px' } }>
						{ iconName && (
							<i className={ iconClasses } style={ { fontSize: '24px' } } />
						) }
						<Button variant="secondary" onClick={ () => setIsIconsModalOpen( true ) }>
							{ iconName
								? __( 'Change icon', 'brandy-blocks' )
								: __( 'Choose icon', 'brandy-blocks' ) }
						</Button>
						{ iconName && (
							<Button
								variant="tertiary"
								isDestructive
								onClick={ () => setAttributes( { iconName: '' } ) }
							>
								{ __( 'Remove', 'brandy-blocks' ) }
							</Button>
						) }
					</div>

					{ iconName && (
						<>
							<SelectControl
								__nextHasNoMarginBottom
								label={ __( 'Position', 'brandy-blocks' ) }
								value={ iconPosition }
								options={ ICON_POSITIONS }
								onChange={ ( value ) => setAttributes( { iconPosition: value } ) }
							/>

							<RangeControl
								__nextHasNoMarginBottom
								label={ __( 'Icon size', 'brandy-blocks' ) }
								help={ __(
									'Reset to size the icon with the badge text.',
									'brandy-blocks'
								) }
								value={ iconSize }
								onChange={ ( value ) => setAttributes( { iconSize: value } ) }
								min={ 4 }
								max={ 64 }
								allowReset
								resetFallbackValue={ undefined }
							/>
						</>
					) }
				</PanelBody>
			</InspectorControls>

			<div { ...blockProps }>
				{ iconPosition !== 'right' && icon }
				<RichText
					tagName="span"
					className="brandy-badge__text"
					value={ text }
					onChange={ ( value ) => setAttributes( { text: value } ) }
					placeholder={ __( 'Badge text…', 'brandy-blocks' ) }
				/>
				{ iconPosition === 'right' && icon }
			</div>

			<IconsModal
				isIconsModalOpen={ isIconsModalOpen }
				setIsIconsModalOpen={ setIsIconsModalOpen }
				icon={ { name: iconName, type: iconType } }
				/*
				 * Only the keys the modal actually sent. It reports a weight
				 * change as `{ type }` alone, and mapping that to a full pair
				 * would write `iconName: undefined` — which resets the
				 * attribute to its default and clears the chosen glyph.
				 */
				setAttributes={ ( newAttrs ) =>
					setAttributes( {
						...( undefined !== newAttrs.name ? { iconName: newAttrs.name } : {} ),
						...( undefined !== newAttrs.type ? { iconType: newAttrs.type } : {} ),
					} )
				}
			/>
		</>
	);
}
